import { T, useGT } from 'gt-react';
import { ArrowSquareOut as OpenIcon } from '@phosphor-icons/react';
import type { ArTaskRow } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { ARInvoiceDetails } from '@/components/accounts-receivable/invoice-detail/ar-invoice-details';
import { SalesOrderDetails } from '@/components/accounts-receivable/sales-order-detail/sales-order-details';
import type { ArTaskRecord } from './arTasksPageState';
import type { ArTaskRecords } from './useArTaskRecords';

const STATUS_CLASS =
  'm-0 rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground';

function RecordHeading({ kind }: { kind: ArTaskRow['kind'] }) {
  return (
    <h3 className="m-0 text-[11px] font-semibold tracking-[0.06em] text-stone-500 uppercase">
      {kind === 'to_send' ? (
        <T context="AR task detail, heading over the task's invoice summary">
          Invoice
        </T>
      ) : (
        <T context="AR task detail, heading over the task's sales order summary">
          Sales order
        </T>
      )}
    </h3>
  );
}

/**
 * Opens the record's full slide-in, with every tab it has. The heading next
 * to it already names the record, so the label is just "Open".
 */
function OpenRecordButton({
  kind,
  onOpen,
}: {
  kind: ArTaskRow['kind'];
  onOpen: () => void;
}) {
  const gt = useGT();
  const label =
    kind === 'to_send'
      ? gt('Open the invoice', {
          context:
            "AR task detail, screen-reader label for opening the task's invoice",
        })
      : gt('Open the sales order', {
          context:
            "AR task detail, screen-reader label for opening the task's sales order",
        });
  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0"
      onClick={onOpen}
      aria-label={label}
    >
      <T context="AR task detail, opens the task's invoice or sales order in its slide-in">
        Open
      </T>
      <OpenIcon aria-hidden className="size-3.5" />
    </Button>
  );
}

/** Shown while the task's record hasn't synced from the ERP into Gently. */
function NotInGentlyYet({ kind }: { kind: ArTaskRow['kind'] }) {
  return (
    <p className={STATUS_CLASS}>
      {kind === 'to_send' ? (
        <T context="AR task detail, the task's invoice has not synced from the ERP yet">
          This invoice isn't in Gently yet. It shows here once it syncs from the
          ERP.
        </T>
      ) : (
        <T context="AR task detail, the task's sales order has not synced from the ERP yet">
          This sales order isn't in Gently yet. It shows here once it syncs from
          the ERP.
        </T>
      )}
    </p>
  );
}

function RecordLoading() {
  return (
    <p className={STATUS_CLASS}>
      <T context="AR task detail, the task's invoice or sales order is loading">
        Loading…
      </T>
    </p>
  );
}

/**
 * The record's own detail view, as its slide-in shows it, without the
 * invoice's activity pane: the task's notes and activity sit below.
 */
function RecordBody({
  kind,
  record,
  records,
}: {
  kind: ArTaskRow['kind'];
  record: ArTaskRecord | null;
  records: ArTaskRecords;
}) {
  if (!record) return <NotInGentlyYet kind={kind} />;
  if (kind === 'to_send') {
    if (!records.invoice) return <RecordLoading />;
    return (
      <div className="rounded-lg border py-3">
        {/* Keyed by record: the detail views' tabs pick their default only
            on mount, so a new task must remount them. */}
        <ARInvoiceDetails
          key={records.invoice.id}
          invoice={records.invoice}
          hideActivity
          preferInvoiceLines
        />
      </div>
    );
  }
  if (!records.salesOrder) return <RecordLoading />;
  return (
    <div className="rounded-lg border py-3">
      <SalesOrderDetails
        key={records.salesOrder.id}
        salesOrder={records.salesOrder}
        lineItems={records.salesOrderLines}
      />
    </div>
  );
}

/**
 * The task's own record: the invoice for a not-sent task, the sales order
 * for a shipped one, shown as the record's detail view. Open shows it in its
 * slide-in, with the linked records' tabs.
 */
export function ArTaskRecordPanel({
  kind,
  record,
  records,
  onOpenRecord,
}: {
  kind: ArTaskRow['kind'];
  /** Null while the record hasn't synced into Gently. */
  record: ArTaskRecord | null;
  /** Each is null while it loads. */
  records: ArTaskRecords;
  onOpenRecord: (record: ArTaskRecord) => void;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <RecordHeading kind={kind} />
        {record && (
          <OpenRecordButton kind={kind} onOpen={() => onOpenRecord(record)} />
        )}
      </div>
      <RecordBody kind={kind} record={record} records={records} />
    </section>
  );
}
